/**
=========================================================
* Soft UI Dashboard React - TypeScript Conversion
=========================================================
*/

import { Theme } from "@mui/material/styles";

// Define the shape of props (ownerState)
interface NavbarOwnerState {
  transparentNavbar?: boolean;
  absolute?: boolean;
  light?: boolean;
  isMini?: boolean;
}

// 1️⃣ Main navbar style
export const navbar = (theme: Theme, ownerState: NavbarOwnerState) => {
  const { palette, boxShadows, functions, transitions, breakpoints, borders } =
    theme as any;
  const { transparentNavbar, absolute, light } = ownerState;

  const { dark, white, text, transparent } = palette;
  const { navbarBoxShadow } = boxShadows;
  const { rgba, pxToRem } = functions;
  const { borderRadius } = borders;

  return {
    boxShadow: transparentNavbar || absolute ? "none" : navbarBoxShadow,
    backdropFilter:
      transparentNavbar || absolute
        ? "none"
        : `saturate(200%) blur(${pxToRem(30)})`,
    backgroundColor:
      transparentNavbar || absolute
        ? `${transparent.main} !important`
        : rgba(white.main, 0.8),

    color: (() => {
      if (light) return white.main;
      if (transparentNavbar) return text.main;
      return dark.main;
    })(),

    top: absolute ? 0 : pxToRem(12),
    minHeight: pxToRem(75),
    display: "grid",
    alignItems: "center",
    borderRadius: borderRadius.xl,
    paddingTop: pxToRem(8),
    paddingBottom: pxToRem(8),
    paddingRight: absolute ? pxToRem(8) : 0,
    paddingLeft: absolute ? pxToRem(16) : 0,

    "& > *": {
      transition: transitions.create("all", {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.standard,
      }),
    },

    "& .MuiToolbar-root": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",

      [breakpoints.up("sm")]: {
        minHeight: "auto",
        padding: `${pxToRem(4)} ${pxToRem(16)}`,
      },
    },
  } as const;
};

// 2️⃣ Navbar container
export const navbarContainer = ({ breakpoints }: Theme) => ({
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "space-between",
  pt: 0.5,
  pb: 0.5,

  [breakpoints.up("md")]: {
    flexDirection: "row",
    alignItems: "center",
    paddingTop: "0",
    paddingBottom: "0",
  },
});

// 3️⃣ Navbar row
export const navbarRow = (
  { breakpoints }: Theme,
  { isMini }: NavbarOwnerState
) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",

  [breakpoints.up("md")]: {
    justifyContent: isMini ? "space-between" : "stretch",
    width: isMini ? "100%" : "max-content",
  },

  [breakpoints.up("xl")]: {
    justifyContent: "stretch !important",
    width: "max-content !important",
  },
});

// 4️⃣ Navbar icon button
export const navbarIconButton = ({ typography, breakpoints }: any) => ({
  px: 0.75,

  "& .material-icons, .material-icons-round": {
    fontSize: `${typography.size?.md || "1rem"} !important`,
  },

  "& .MuiTypography-root": {
    display: "none",

    [breakpoints.up("sm")]: {
      display: "inline-block",
      lineHeight: 1.2,
      ml: 0.5,
    },
  },
});

// 5️⃣ Mobile menu
export const navbarMobileMenu = ({ breakpoints }: Theme) => ({
  display: "inline-block",
  lineHeight: 0,

  [breakpoints.up("xl")]: {
    display: "none",
  },
});
